import React, { useEffect, useMemo, useState } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import { SlidersHorizontal, ChevronRight } from "lucide-react";
import ProductCard from "@/components/ProductCard";
import api from "@/lib/api";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";

const Shop = () => {
  const { categorySlug } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);

  const search = searchParams.get("search") || "";
  const brand = searchParams.get("brand") || "";
  const subcategory = searchParams.get("subcategory") || "";
  const sort = searchParams.get("sort") || "featured";

  useEffect(() => {
    api.get("/categories").then((res) => {
      setCategories(res.data.categories || []);
      setBrands(res.data.brands || []);
    });
  }, []);

  useEffect(() => {
    setLoading(true);
    const params = { limit: 48, sort };
    if (categorySlug) params.category = categorySlug;
    if (search) params.search = search;
    if (brand) params.brand = brand;
    if (subcategory) params.subcategory = subcategory;
    api.get("/products", { params })
      .then((res) => {
        setProducts(res.data.items || []);
        setTotal(res.data.total || 0);
      })
      .finally(() => setLoading(false));
  }, [categorySlug, search, brand, subcategory, sort]);

  const currentCategory = useMemo(
    () => categories.find((c) => c.slug === categorySlug),
    [categories, categorySlug]
  );

  const title = currentCategory
    ? currentCategory.name
    : search
    ? `Search results for "${search}"`
    : "All Products";

  const updateParam = (key, value) => {
    const next = new URLSearchParams(searchParams);
    if (value && value !== "all") next.set(key, value);
    else next.delete(key);
    setSearchParams(next);
  };

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      {/* Breadcrumb */}
      <nav className="flex items-center text-xs text-slate-500 mb-4">
        <Link to="/" className="hover:text-[#0b3d75]">Home</Link>
        <ChevronRight className="w-3 h-3 mx-1" />
        <Link to="/shop" className="hover:text-[#0b3d75]">Shop</Link>
        {currentCategory && (
          <>
            <ChevronRight className="w-3 h-3 mx-1" />
            <span className="text-slate-900 font-medium">{currentCategory.name}</span>
          </>
        )}
      </nav>

      <h1 className="text-3xl md:text-4xl font-black text-slate-900 tracking-tight">{title}</h1>
      <p className="text-slate-500 mt-1">{total} product{total === 1 ? "" : "s"} available</p>

      <div className="mt-8 grid lg:grid-cols-[240px_1fr] gap-8">
        {/* Filters */}
        <aside className="space-y-6">
          <div>
            <h3 className="font-bold text-slate-900 mb-3 flex items-center gap-2">
              <SlidersHorizontal className="w-4 h-4" /> Categories
            </h3>
            <ul className="space-y-1.5 text-sm">
              <li>
                <Link to="/shop" className={`block px-2 py-1.5 rounded-md hover:bg-slate-100 ${!categorySlug ? "text-[#0b3d75] font-semibold bg-slate-100" : "text-slate-700"}`}>
                  All Categories
                </Link>
              </li>
              {categories.map((c) => (
                <li key={c.slug}>
                  <Link to={`/shop/${c.slug}`} className={`block px-2 py-1.5 rounded-md hover:bg-slate-100 ${categorySlug === c.slug ? "text-[#0b3d75] font-semibold bg-slate-100" : "text-slate-700"}`}>
                    {c.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {currentCategory && currentCategory.subcategories?.length > 0 && (
            <div>
              <h3 className="font-bold text-slate-900 mb-3">Subcategory</h3>
              <ul className="space-y-2 text-sm">
                {currentCategory.subcategories.map((sc) => (
                  <li key={sc.slug} className="flex items-center gap-2">
                    <Checkbox
                      id={`sc-${sc.slug}`}
                      checked={subcategory === sc.slug}
                      onCheckedChange={(v) => updateParam("subcategory", v ? sc.slug : "")}
                    />
                    <label htmlFor={`sc-${sc.slug}`} className="cursor-pointer text-slate-700">
                      {sc.name}
                    </label>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div>
            <h3 className="font-bold text-slate-900 mb-3">Brand</h3>
            <ul className="space-y-2 text-sm max-h-64 overflow-y-auto pr-2">
              {brands.map((b) => (
                <li key={b} className="flex items-center gap-2">
                  <Checkbox
                    id={`b-${b}`}
                    checked={brand === b}
                    onCheckedChange={(v) => updateParam("brand", v ? b : "")}
                  />
                  <label htmlFor={`b-${b}`} className="cursor-pointer text-slate-700">
                    {b}
                  </label>
                </li>
              ))}
            </ul>
          </div>

          {(brand || subcategory || search) && (
            <Button variant="outline" className="w-full" onClick={() => setSearchParams({})}>
              Clear filters
            </Button>
          )}
        </aside>

        {/* Grid */}
        <div>
          <div className="flex items-center justify-between mb-4">
            <div className="text-sm text-slate-600">Showing {products.length} of {total}</div>
            <div className="flex items-center gap-2">
              <span className="text-sm text-slate-600 hidden sm:block">Sort by:</span>
              <Select value={sort} onValueChange={(v) => updateParam("sort", v)}>
                <SelectTrigger className="w-48">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="featured">Featured</SelectItem>
                  <SelectItem value="price_asc">Price: Low to High</SelectItem>
                  <SelectItem value="price_desc">Price: High to Low</SelectItem>
                  <SelectItem value="name">Name: A → Z</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {loading ? (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {Array.from({ length: 6 }).map((_, i) => (
                <Skeleton key={i} className="aspect-[3/4] w-full" />
              ))}
            </div>
          ) : products.length === 0 ? (
            <div className="text-center py-16 bg-slate-50 rounded-xl">
              <div className="text-slate-900 font-bold text-lg">No products found</div>
              <div className="text-slate-500 mt-1">Try adjusting your filters or search terms.</div>
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {products.map((p) => (
                <ProductCard key={p.id} product={p} />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Shop;
